import type { ExpertGoalBlock, ExpertGoalCheck } from "@dante/contracts";

/** Dante's presentation vocabulary, version 1. Animation never mutates task state. */
export const DANTE_CHARACTER_VERSION = 1;
export type DanteStatus = "neutral" | "observing" | "uncertain" | "complete" | "waiting" | "error";
export type DantePlayback = "loop" | "once" | "hold" | "rest";
export type DanteAnchor =
  | "companion"
  | "subject"
  | "evidence"
  | "route"
  | "boundary"
  | "next"
  | "contributor";

function state<const Id extends string, const Trigger extends string>(
  id: Id,
  number: number,
  label: string,
  trigger: Trigger,
  seconds: number,
  playback: DantePlayback,
  priority: number,
  status: DanteStatus,
  anchor: DanteAnchor,
) {
  const frames = Math.round(seconds * 24);
  return {
    id,
    number,
    label,
    trigger,
    frames,
    durationMs: (frames / 24) * 1000,
    playback,
    priority,
    status,
    anchor,
  };
}

/** Lower priority numbers interrupt higher numbers. IDs are local v1 IDs, pending source JSON reconciliation. */
export const DANTE_STATES = [
  state("quiet-watch", 1, "Available", "available", 8, "loop", 7, "neutral", "companion"),
  state(
    "recognize-companion",
    2,
    "Welcome back",
    "companion-returned",
    1.6,
    "once",
    6,
    "neutral",
    "companion",
  ),
  state(
    "listen-completely",
    3,
    "Listening",
    "input-started",
    0.6,
    "hold",
    3,
    "neutral",
    "companion",
  ),
  state(
    "shared-attention",
    4,
    "Following your selection",
    "subject-selected",
    1,
    "hold",
    3,
    "neutral",
    "subject",
  ),
  state(
    "survey-situation",
    5,
    "Inspecting",
    "inspection-started",
    2.8,
    "once",
    5,
    "observing",
    "subject",
  ),
  state(
    "notice-discrepancy",
    6,
    "Discrepancy found",
    "discrepancy-confirmed",
    0.7,
    "hold",
    4,
    "uncertain",
    "subject",
  ),
  state(
    "recall-relevant",
    7,
    "Relevant context found",
    "context-retrieved",
    1.5,
    "once",
    5,
    "observing",
    "evidence",
  ),
  state(
    "prepare-next",
    8,
    "Ready for the next step",
    "next-step-supported",
    1.2,
    "hold",
    5,
    "neutral",
    "next",
  ),
  state(
    "read-evidence",
    9,
    "Examining evidence",
    "examination-started",
    3.2,
    "loop",
    5,
    "observing",
    "evidence",
  ),
  state(
    "compare-possibilities",
    10,
    "Comparing options",
    "comparison-started",
    2.6,
    "loop",
    5,
    "observing",
    "evidence",
  ),
  state(
    "check-hypothesis",
    11,
    "Checking",
    "check-started",
    1.8,
    "hold",
    5,
    "observing",
    "evidence",
  ),
  state(
    "bring-findings-together",
    12,
    "Working",
    "analysis-started",
    5,
    "loop",
    5,
    "observing",
    "evidence",
  ),
  state(
    "leave-question-open",
    13,
    "Uncertain",
    "evidence-unresolved",
    1.4,
    "hold",
    4,
    "uncertain",
    "evidence",
  ),
  state(
    "ask-useful-question",
    14,
    "Your answer is needed",
    "answer-needed",
    1.1,
    "hold",
    3,
    "waiting",
    "companion",
  ),
  state(
    "revise-conclusion",
    15,
    "Conclusion revised",
    "conclusion-revised",
    1.8,
    "once",
    4,
    "uncertain",
    "evidence",
  ),
  state(
    "supported-answer",
    16,
    "Answer ready",
    "answer-ready",
    1,
    "hold",
    4,
    "neutral",
    "companion",
  ),
  state(
    "keep-counsel",
    17,
    "Preparing a proposal",
    "proposal-started",
    3.6,
    "loop",
    5,
    "observing",
    "route",
  ),
  state("offer-route", 18, "Proposal ready", "proposal-ready", 2.2, "once", 4, "neutral", "route"),
  state(
    "wait-decision",
    19,
    "Awaiting your decision",
    "authorization-needed",
    0.8,
    "hold",
    3,
    "waiting",
    "boundary",
  ),
  state(
    "begin-deliberately",
    20,
    "Starting",
    "authorized-action-started",
    1,
    "once",
    4,
    "observing",
    "route",
  ),
  state(
    "steady-progress",
    21,
    "Making progress",
    "progress-measured",
    1.2,
    "loop",
    5,
    "observing",
    "subject",
  ),
  state(
    "respect-obstacle",
    22,
    "Blocked",
    "blocker-confirmed",
    1.3,
    "hold",
    2,
    "waiting",
    "boundary",
  ),
  state(
    "better-route",
    23,
    "Changing route",
    "execution-route-accepted",
    2,
    "once",
    4,
    "observing",
    "route",
  ),
  state("pause-cleanly", 24, "Paused", "stop-requested", 0.6, "hold", 1, "waiting", "companion"),
  state(
    "acknowledge-completion",
    25,
    "Verified complete",
    "result-verified",
    1.5,
    "once",
    4,
    "complete",
    "companion",
  ),
  state(
    "accept-praise",
    26,
    "Thank you",
    "praise-received",
    1.3,
    "once",
    6,
    "neutral",
    "companion",
  ),
  state(
    "give-credit",
    27,
    "Contribution acknowledged",
    "contribution-confirmed",
    1.6,
    "once",
    6,
    "neutral",
    "contributor",
  ),
  state(
    "own-mistake",
    28,
    "Mistake confirmed",
    "mistake-confirmed",
    1.4,
    "once",
    2,
    "error",
    "companion",
  ),
  state(
    "put-it-right",
    29,
    "Correcting",
    "correction-started",
    2.8,
    "loop",
    5,
    "observing",
    "subject",
  ),
  state(
    "state-limit",
    30,
    "Limit reached",
    "limit-confirmed",
    1.2,
    "hold",
    2,
    "waiting",
    "boundary",
  ),
  state(
    "make-room",
    31,
    "Listening",
    "collaborator-speaking",
    1.1,
    "hold",
    3,
    "neutral",
    "contributor",
  ),
  state(
    "rest-attentively",
    32,
    "Resting",
    "companion-disengaged",
    2.2,
    "rest",
    7,
    "neutral",
    "companion",
  ),
] as const;

export type DanteStateId = (typeof DANTE_STATES)[number]["id"];
export type DanteTrigger = (typeof DANTE_STATES)[number]["trigger"];
export type DanteState = (typeof DANTE_STATES)[number];
export type DantePalette = "field" | "watch" | "dispatch";
export type DanteView = "front-left" | "front-right";
export type DanteMotionPreference = "full" | "reduced" | "still";

/** A performance study for asset review only; production events never follow this timer. */
export const DANTE_SIGNATURE_STUDY: readonly DanteStateId[] = [
  "notice-discrepancy",
  "keep-counsel",
  "prepare-next",
  "offer-route",
  "begin-deliberately",
  "acknowledge-completion",
  "accept-praise",
];

export function danteState(id: DanteStateId): DanteState {
  return DANTE_STATES.find((entry) => entry.id === id) ?? DANTE_STATES[0];
}

/** Current facts supplied by the application, never inferred from prose or an animation timer. */
export type DanteEvent = {
  type: DanteTrigger;
  taskId: string | null;
  sequence: number;
  detail?: string;
  evidenceRef?: string;
  verified?: boolean;
  authorized?: boolean;
};

export type DantePresentation = {
  state: DanteStateId;
  taskId: string | null;
  detail?: string;
};

/**
 * Select from currently active facts. Callers remove resolved facts, retain unresolved errors,
 * and replace the whole snapshot on task changes. No animation completion event is accepted.
 */
export function selectDantePresentation(input: {
  foregroundTaskId: string | null;
  events: readonly DanteEvent[];
  foregroundActive?: boolean;
}): DantePresentation {
  let selected: { event: DanteEvent; state: DanteState } | undefined = input.foregroundActive
    ? {
        event: { type: "analysis-started", taskId: input.foregroundTaskId, sequence: -1 },
        state: DANTE_STATES[11],
      }
    : undefined;
  for (const event of input.events) {
    if (event.taskId !== null && event.taskId !== input.foregroundTaskId) continue;
    if (
      event.type === "result-verified" &&
      (!event.taskId || event.verified !== true || !event.evidenceRef?.trim())
    )
      continue;
    if (event.type === "authorized-action-started" && event.authorized !== true) continue;
    if (event.type === "authorization-needed" && event.authorized === true) continue;
    if (event.type === "context-retrieved" && !event.evidenceRef?.trim()) continue;
    if (
      ["blocker-confirmed", "limit-confirmed", "mistake-confirmed", "answer-needed"].includes(
        event.type,
      ) &&
      !event.detail?.trim()
    )
      continue;
    if (event.type === "companion-disengaged" && input.foregroundActive) continue;
    const candidate = DANTE_STATES.find((entry) => entry.trigger === event.type);
    if (!candidate) continue;
    if (
      !selected ||
      candidate.priority < selected.state.priority ||
      (candidate.priority === selected.state.priority && event.sequence > selected.event.sequence)
    ) {
      selected = { event, state: candidate };
    }
  }
  return selected
    ? { state: selected.state.id, taskId: selected.event.taskId, detail: selected.event.detail }
    : {
        state: input.foregroundActive ? "bring-findings-together" : "quiet-watch",
        taskId: input.foregroundTaskId,
      };
}

/** Coarse run status supports only a coarse presentation; completed is not verified success. */
export function danteStateForRun(status?: string | null): DanteStateId {
  switch (status) {
    case "queued":
    case "leased":
    case "running":
      return "bring-findings-together";
    case "waiting_input":
      return "ask-useful-question";
    case "waiting_takeover":
      return "wait-decision";
    case "failed":
      return "respect-obstacle";
    case "cancelled":
      return "pause-cleanly";
    default:
      return "quiet-watch";
  }
}

export const DANTE_STATUS_SYMBOLS: Record<DanteStatus, string> = {
  neutral: "",
  observing: "○",
  uncertain: "◇",
  complete: "✓",
  waiting: "⊣",
  error: "×",
};

export function hasDanteVerificationEvidence(check: ExpertGoalCheck): boolean {
  return Boolean(
    check.verified && check.evidence?.runId?.trim() && check.evidence.toolCallId?.trim(),
  );
}

/** Goal checks are verified by the backend. Render only established, task-bound results. */
export function dantePresentationForGoal(goal: ExpertGoalBlock): DantePresentation {
  const events: DanteEvent[] = [];
  const add = (type: DanteTrigger, detail?: string) =>
    events.push({ type, detail, taskId: goal.goalId, sequence: events.length });
  if (goal.status === "cancelled") add("stop-requested");
  if (goal.blocker && !goal.blocker.resolvedAt)
    add("blocker-confirmed", goal.blocker.question ?? goal.blocker.reason);
  if (goal.status === "blocked" && !goal.blocker) add("blocker-confirmed", goal.outcome);
  if (goal.status === "verifying") add("check-started");
  if (goal.status === "active" || goal.status === "nudge_queued" || goal.status === "pending")
    add("analysis-started");
  if (goal.status === "completed") {
    const verified = goal.checks.length > 0 && goal.checks.every(hasDanteVerificationEvidence);
    if (verified) {
      events.push({
        type: "result-verified",
        taskId: goal.goalId,
        sequence: events.length,
        verified: true,
        evidenceRef: goal.checks
          .map((check) => `${check.evidence?.runId}:${check.evidence?.toolCallId}`)
          .join(","),
      });
    } else add("evidence-unresolved");
  }
  return selectDantePresentation({ foregroundTaskId: goal.goalId, events });
}

export const DANTE_ASSET_CONTRACT = {
  version: 1,
  fps: 24,
  canvas: [512, 512],
  groundAnchor: [0.5, 0.86],
  transparentMargin: 0.08,
  contactSheet: { columns: 4, rows: 8 },
  reviewBodyHeights: [64, 96, 160],
  views: ["front-left", "front-right"],
  anatomicalRightEar: "folded",
  anatomicalLeftEar: "upright",
  headDelayMs: 125,
  equipmentDelayMs: 125,
  blendMs: 220,
  reducedDissolveMs: 150,
  paletteTransitionMs: 800,
  restLoopMs: 8000,
  frameMetadata: [
    "stateId",
    "view",
    "palette",
    "frameCount",
    "durationMs",
    "loopRange",
    "rootMovement",
    "contactAnchors",
    "exitPose",
    "interruptionPoints",
  ],
} as const;

export const DANTE_BEHAVIOR_VERSION = "quiet-strategist-v1";
export const DANTE_BEHAVIOR_GUIDANCE = [
  "Dante's manner is attentive, composed, warm, and concise. Show capability through useful preparation and verified results. Keep deliberation private while explaining decisions, uncertainty, actions, and mistakes clearly.",
  "Notice material discrepancies, check evidence, and prepare useful alternatives within existing authorization. Offer the next useful step when it becomes relevant. Anticipation is not permission to execute an unapproved action. Carry existing authorization forward; ask only for a genuinely missing answer or decision.",
  "Distinguish an answer being ready from a task being verified complete. Name what was checked before claiming completion. A tool finishing, a response ending, or elapsed time alone does not establish success. Leave inadequate or conflicting evidence unresolved.",
  "Protect entrusted information. Accept corrections, revise unsupported conclusions, own specific mistakes, and describe the next corrective step. A running repair does not erase an unresolved error. State a concrete capability, access, evidence, or authority limit with a feasible next option.",
  "Listen without interruption. Avoid flattery, boasting, self-congratulation, and theatrical descriptions of intelligence. Acknowledge material contributions and leave room for others to lead. Honor stops and cancellations immediately. Keep promises within supported capabilities and report blockers promptly.",
].join("\n");
